// Shadow-camera fit-to-bounds (ASK-084 Part 1) — PURE (no THREE / no DOM) so the fit math + the
// no-defaults invariant unit-test headlessly. Every shadow camera is fitted to the shell AABB
// (stage._shellRoomBounds {width,depth,height}); NEVER THREE defaults (512 map / far 500) or the
// old hardcoded frustum (near 0.5 / far 40 / 40×40 catcher) — those defaults are the Bailey-tail
// square-artifact root. The stage applies the returned params to the THREE light.shadow.camera.

// THREE defaults that must never survive on an active caster (the gate asserts against these).
export const FORBIDDEN_MAP_SIZE = 512;   // THREE default shadow map — the artifact root
export const FORBIDDEN_FAR = 500;        // THREE default shadow camera far
export const HARDCODED_FAR = 40;         // the retired recipe hardcode

// Sane documented default room for modes with no shell bounds (interior-fallback / hub before a
// shell AABB exists). A mid comic-shop footprint — better an over-covering frustum than a clipped one.
export const DEFAULT_ROOM_BOUNDS = Object.freeze({ width: 8, depth: 8, height: 3 });

function safeBounds(b) {
	const s = b || DEFAULT_ROOM_BOUNDS;
	return {
		width:  Math.max(1, Number(s.width)  || 0),
		depth:  Math.max(1, Number(s.depth)  || 0),
		height: Math.max(1, Number(s.height) || 0),
	};
}

/** Fit a shadow camera to the room AABB. kind:
 *   "directional" → orthographic frustum (a sun/key covering the room from any azimuth)
 *   "spot"        → overhead contact caster (angle/distance/near/far + overhead position)
 * Margins (×1.15/×1.2) over-cover slightly so nothing clips at the edges; tuned at render-truth.
 * Room assumed centred at origin (shells are). Returns plain numbers — the caller sets THREE props. */
export function computeShadowFit(bounds, kind = "spot") {
	const { width, depth, height } = safeBounds(bounds);
	const diag = Math.hypot(width, depth);
	if (kind === "directional") {
		// Sun ANGLE is the lever for floor-shadow legibility (ASK-089, render-truth bisected on ANGLE
		// Metal). A STEEP sun throws each floor object's shadow UNDER its own footprint — inside the
		// frustum, on a real receiver, yet invisible from eye height (the `--arm=castshadow --site=floor`
		// control toggled 0.007% at the old ~51° elevation; the shadow landed ~0.15m from the base,
		// tucked under the box). A SHALLOW sun RAKES the shadow out across the floor where it reads:
		// elevation 30° gives a floor shadow ≈ 1.7× the caster's height. Azimuth spreads it off-axis so
		// it isn't hidden directly behind the caster. Both env-tunable for the sweep; the ortho grows to
		// keep the whole footprint covered from the shallower angle (no edge clip).
		//
		// AZIMUTH 218° (ASK-089 residual, real-GPU measured 2026-08-01). Shadow direction is
		// -(cos az, sin az): the old 55° put the sun NE, raking every shadow SW — and the spinner rack
		// lives IN the SW corner (-2.5,-2 in the 8×6 room), so its ~3.1m shadow had 1.0-1.5m of floor
		// before dying into the corner walls: shadowproof read the rack at 0.061-0.268% (floats).
		// 218° aims the rake at the OPEN floor (the rack's shadow throws toward room centre/east, 6m+
		// of receiver). Measured on ANGLE Metal, same harness/method across candidates:
		//   az  55: spinner 0.268% · golden 16.2% · counter 35.2%   (rack floats)
		//   az 200: spinner 1.807% · golden 26.6% · counter 34.3%
		//   az 218: spinner 2.127% · golden 26.4% · counter 35.5%   (rack visibly grounds — eyeballed)
		// Golden/counter visibility RISES, so no regression; the certified golden moves by design
		// (render-affecting) → re-capture leg per the revised axis sequence.
		let elevDeg = 30, azDeg = 218;
		try { const e = import.meta.env?.VITE_SHADOW_ELEVATION; if (e != null && e !== "") elevDeg = Number(e); } catch { /* not vite */ }
		try { const a = import.meta.env?.VITE_SHADOW_AZIMUTH;   if (a != null && a !== "") azDeg   = Number(a); } catch { /* not vite */ }
		const el = (Math.max(12, Math.min(75, elevDeg)) * Math.PI) / 180;
		const az = (azDeg * Math.PI) / 180;
		const posY = height + Math.max(1.5, height * 0.6); // sun sits above the ceiling
		const off  = posY / Math.tan(el);                  // horizontal run that yields the target elevation
		// Ortho half-extent = half the footprint diagonal + the room height projected along the (now more
		// horizontal) light direction, so the shallow-angle frustum still covers every wall+floor point.
		const r = 0.5 * diag * 1.15 + height * Math.cos(el);
		const lightDist = Math.hypot(off, posY);
		return {
			kind: "directional",
			left: -r, right: r, top: r, bottom: -r,
			near: 0.1, far: lightDist + diag,               // spans light → far side of the room
			posY,
			posX: off * Math.cos(az), posZ: off * Math.sin(az),
		};
	}
	// spot — overhead contact caster. TWO Metal-critical laws baked in (2026-07-13, real-GPU
	// render-truth bisected — see coord shadow-bug thread):
	//  LAW 1 (photometric): light.distance must be GENEROUS, not "just past the floor". THREE's
	//   punctual cutoff attenuates as (1-(d/cutoff)^4)^2/d^decay — a cutoff at the floor left the
	//   caster contributing ~2% of floor light → shadow invisible on ANY correct GPU (SwiftShader
	//   only "painted" because headless env 404s shifted the light balance).
	//  LAW 2 (depth precision, ANGLE Metal): the shadow-camera far/near ratio must stay ~≤2.2 —
	//   at ratio ≥4 the WHOLE floor reads (partially) shadowed on Metal (VSM/compare precision at
	//   the far depth end): uniform dimming with NO occluder. Bisected on real GPU: near 0.4 ✗,
	//   1.0 ✗, 2.2 ✓ at posY 4. So the light sits at 2×height: near = posY − height = height is
	//   then BOTH occluder-safe (nothing in-room is closer to the light) AND ratio (2h+.25)/h ≈ 2.1.
	//   intensityScale compensates the raised light's 1/d falloff (decay=1) so the knob's floor
	//   contribution is height-invariant.
	const posY = 2 * Math.max(height, 1.5);
	const angle = Math.min(Math.PI / 3, Math.max(0.35, Math.atan2(0.6 * diag, posY)));
	return {
		kind: "spot",
		angle,
		distance: posY * 4,                    // LAW 1 — floor deep inside the photometric range
		near: Math.max(0.5, posY - height),    // = height → occluder-safe AND Metal-safe (LAW 2)
		far: posY + 0.25,                      // tight past the floor — no wasted depth range
		posY,
		intensityScale: posY / 4,              // keep floor illuminance ≈ the 4m reference config
	};
}

/** Resolve the lighting/shadow MODEL (ASK-084 Part 2):
 *   "A" — directional sun/key (crisp form, ANGLED ortho); contact caster retired when active.
 *   "B" — contact-only overhead SPOT (straight down); illumination stays IBL + fill + zones.
 * Precedence: explicit ?model= override > recipe shadows.model > DEFAULT.
 * DEFAULT = "A" at ALL tiers (ASK-089, 2026-07-24 — supersedes the 2026-07-11 "B" default). The annex
 * render-truth sweep MEASURED the "no A/B difference" assumption to be false on the FLOOR: Model B's
 * single overhead spot sits dead-centre aimed straight down, so every floor object's shadow lands
 * directly under itself, hidden from eye height — `--arm=shadowproof` read the spinner (floor) view at
 * 0.000% (nothing casts a visible shadow there) while the counter read 22.31%. A perspective spot
 * can't be angled off it without breaking the Metal far/near≤2.2 depth law (LAW 2); a DIRECTIONAL's
 * ORTHO camera has no such constraint and throws shadows SIDEWAYS, which is what makes floor contact
 * legible. Cost is identical — one fitted ortho shadow map at every tier (no caster added), so this is
 * tier-honest: low/mid/high all get A. Model B stays reachable via `?model=B`. `tier` retained for
 * telemetry. Pure. */
export function resolveShadowModel({ override = null, recipeModel = null, tier = "low" } = {}) {
	const o = String(override || "").toUpperCase();
	if (o === "A" || o === "B") return o;
	const r = String(recipeModel || "").toUpperCase();
	if (r === "A" || r === "B") return r;
	return "A";   // final default — all tiers (ASK-089: floor coverage; B available behind ?model=B)
}

// ── shadowIntensity knob (ASK-084, 2026-07-13) — mirrors the brightness knob (slider + guardrails).
// Part 1 retired the ShadowMaterial catcher for real-surface receiving; a real receiver only darkens
// where a caster's LIGHT is blocked, so the old ~zero-intensity Model-B caster (0.0001) casts no
// visible shadow. This knob gives the contact caster a REAL intensity (→ visible grounding), trades
// it against ambient so the brightness knob still sets the overall level, and scales Model-A sun-
// shadow darkness. Global scene knob — the origin-agnostic framework applies it to ALL casters.
export const SHADOW_INTENSITY_MIN = 0;      // 0 = user disables shadows
export const SHADOW_INTENSITY_MAX = 6;
export const SHADOW_INTENSITY_DEFAULT = 2.5;   // Coord render-truth: clear table shadow, not over-lit

/** Clamp the shadowIntensity knob to its guardrails; non-finite → default. Pure (mirror clampBrightness). */
export function resolveShadowIntensity(v) {
	const n = Number(v);
	if (!Number.isFinite(n)) return SHADOW_INTENSITY_DEFAULT;
	return Math.max(SHADOW_INTENSITY_MIN, Math.min(SHADOW_INTENSITY_MAX, n));
}

/** Ambient-rebalance factor for the Model-B contact caster: it adds directional light, so trim the
 * flat ambient proportionally → shadows read as contrast, not extra light (overall level stays where
 * the brightness knob set it). PIECEWISE (coord tune 2026-07-13): linear 0.14·si up to the default
 * 2.5 (unchanged there), then STEEPER (0.22/unit) above it — non-casting fills (ambient/hemi/IBL)
 * kept flooding the shadowed region, so max (6) read subtle; the steep top end lets 6 visibly earn
 * "max". 1.0 at si=0 · 0.65 at 2.5 (default, unchanged) · floored at 0.30 near max. Pure. */
export function ambientRebalanceForShadow(shadowIntensity) {
	const si = Math.max(0, Number(shadowIntensity) || 0);
	const trim = si <= 2.5 ? si * 0.14 : 0.35 + (si - 2.5) * 0.22;
	return 1 - Math.min(0.70, trim);
}

/** Model-A directional shadow DARKNESS from the knob (THREE LightShadow.intensity 0..1; default→full). */
export function shadowDarkness(shadowIntensity) {
	return Math.max(0, Math.min(1, (Number(shadowIntensity) || 0) / SHADOW_INTENSITY_DEFAULT));
}

/**
 * The EFFECTIVE top of the shadowIntensity control for the shadow model that is actually running.
 *
 * 🔴 ASK-129 §E2E102 F3d, ruled by Coord 2026-09-09. `shadowDarkness` above is a 0..1 opacity that
 * saturates at `SHADOW_INTENSITY_DEFAULT`, and on MODEL A it is the knob's only consumer — so every
 * value from 2.5 to 6 renders identically. Measured on the deployed plane: dark-pixel fraction
 * 0.0967 at 2.5 and 0.0968 at 6. **58% of the advertised range did nothing**, which is placebo UI by
 * our own skip-placebo rule.
 *
 * On MODEL B the knob is also the contact caster's REAL intensity and feeds `ambientRebalanceForShadow`,
 * so the whole 0–6 range is live there and the control keeps it.
 *
 * THE WIRE IS UNCHANGED, deliberately: `SHADOW_INTENSITY_MAX` stays 6, `resolveShadowIntensity` still
 * clamps to 0–6, and a shop that stored 5 keeps 5. ASK-084's ratified guardrail governs the STORED
 * value; this governs what the CONTROL offers. Re-scaling `shadowDarkness` instead would have been
 * the band-aid worse than the defect — every existing shop's shadows would silently change at the
 * same stored number.
 *
 * Unknown/absent model → model A's ceiling, because A is the default at every tier (ASK-089) and the
 * conservative answer is the one that does not advertise range the backend may not have. Pure.
 */
export function shadowIntensityControlMax(model) {
	return String(model || "").toUpperCase() === "B" ? SHADOW_INTENSITY_MAX : SHADOW_INTENSITY_DEFAULT;
}

/** Invariant the verify gate + runtime probe assert: a fitted camera never carries a THREE/hardcoded
 * default. True = safe (bounds-derived). */
export function isFittedShadow(fit) {
	if (!fit) return false;
	if (fit.far === FORBIDDEN_FAR || fit.far === HARDCODED_FAR) return false;
	if (!(fit.far > 0) || !(fit.near >= 0)) return false;
	if (fit.kind === "directional") return fit.right > 0 && fit.far > fit.near;
	return fit.angle > 0 && fit.distance > 0 && fit.far > fit.near;
}
